import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DeltaBadge } from '~/components/common/delta-badge';
import { InfoTooltip } from '~/components/common/info-tooltip/info-tooltip';
import { Card, CardContent } from '~/components/ui/card';

/** A plain headline number, optionally with a trailing suffix (e.g. "of 213"). */
export interface PerformanceSignalsValueCard {
  kind: 'value';
  label: string;
  tooltip?: string;
  description: string;
  value: string;
  valueSuffix?: string;
}

/** A before → after pair rendered inline with a delta badge. */
export interface PerformanceSignalsDeltaCard {
  kind: 'delta';
  label: string;
  tooltip?: string;
  description: string;
  before: string;
  after: string;
  delta: number;
  deltaUnit?: string;
}

export type PerformanceSignalsStatCard = PerformanceSignalsValueCard | PerformanceSignalsDeltaCard;

interface Props {
  cards: PerformanceSignalsStatCard[];
}

/**
 * The Performance Signals tab's 4-card headline row: what enrichment added,
 * at a glance. Unlike the generic `CoverageStatsRow` (a plain label/value/sub
 * shape), these cards need an inline colored before→after value with a delta
 * badge for two of the four metrics, plus a plain descriptive sentence below
 * every card — so they get their own small card component instead.
 */
export function PerformanceSignalsStatsRow({ cards }: Props) {
  const { t } = useTranslation('catalog-operations');

  return (
    <div
      data-testid="batch-detail-performance-stats"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      {cards.map((card, index) => (
        <Card
          key={card.label}
          data-testid={`batch-detail-performance-stat-card-${String(index)}`}
          className="shadow-stat-card"
        >
          <CardContent className="space-y-2 p-5">
            <div className="flex items-center gap-1">
              <p className="text-muted-foreground text-sm font-medium">{card.label}</p>
              {card.tooltip && (
                <InfoTooltip
                  content={card.tooltip}
                  ariaLabel={t('coverageStatsRow.infoLabel', { label: card.label })}
                  testId={`batch-detail-performance-stat-card-${String(index)}-info-tooltip`}
                />
              )}
            </div>

            {card.kind === 'value' ? (
              <p className="flex items-baseline gap-1.5">
                <span
                  data-testid={`batch-detail-performance-stat-value-${String(index)}`}
                  className="text-2xl font-bold tracking-tight tabular-nums"
                >
                  {card.value}
                </span>
                {card.valueSuffix && (
                  <span className="text-muted-foreground text-sm font-medium">
                    {card.valueSuffix}
                  </span>
                )}
              </p>
            ) : (
              <div className="flex items-center gap-2">
                <span
                  data-testid={`batch-detail-performance-stat-before-value-${String(index)}`}
                  className="text-warning text-2xl font-bold tabular-nums"
                >
                  {card.before}
                </span>
                <ArrowRight className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden="true" />
                <span
                  data-testid={`batch-detail-performance-stat-after-value-${String(index)}`}
                  className="text-primary text-2xl font-bold tabular-nums"
                >
                  {card.after}
                </span>
                <DeltaBadge
                  testId={`batch-detail-performance-stat-delta-${String(index)}`}
                  delta={card.delta}
                  {...(card.deltaUnit ? { unit: card.deltaUnit } : {})}
                />
              </div>
            )}

            <p className="text-muted-foreground text-xs font-light">{card.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
